import { useMemo, useState } from "react";
import { Checkbox, Input } from "antd";
import { ChevronDown } from "lucide-react";
import styled from "styled-components";
import {
	AppButton,
	DynamicModal,
	Icon,
	getColumnValue,
	getLooseColumnKey,
	getLooseDataIndex,
	toValueArray,
	useProcessedData,
	useTableFilters,
	type CustomTableColumn,
	type TableFilterState,
} from "@/shared/components";
import { getCountryFlagUrl } from "@/shared/utils/formatters";
import { TAP_TARGET } from "@/shared/theme/breakpoints";
import { pressedDarken, pressedFade, pressedTint } from "@/shared/theme/pressed";

/** Up to this many options render as chips; longer lists become a searchable checklist. */
const CHIP_OPTION_LIMIT = 8;
const PURPLE = "#3a0ca3";

const FilterButton = styled(AppButton)`
	height: ${TAP_TARGET}px;
`;

const ActiveCount = styled.span`
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	border-radius: 999px;
	background: ${PURPLE};
	color: #ffffff;
	font-size: 12px;
	line-height: 20px;
	text-align: center;
`;

const SheetIntro = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
	color: #707070;
	font-size: 14px;
`;

const ClearAll = styled.button`
	min-height: ${TAP_TARGET}px;
	padding: 0;
	border: 0;
	background: transparent;
	color: ${PURPLE};
	font: inherit;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;

	&:disabled {
		color: #b9b9b9;
		cursor: default;
	}

	${pressedFade}
`;

const Sections = styled.div`
	border-radius: 16px;
	background: #ffffff;
	overflow: hidden;
`;

const Section = styled.section`
	& + & {
		border-top: 1px solid #ebeefd;
	}
`;

const SectionHeader = styled.button<{ $open: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: 56px;
	padding: 8px 16px;
	border: 0;
	background: transparent;
	font: inherit;
	text-align: left;
	cursor: pointer;

	svg {
		flex-shrink: 0;
		color: #707070;
		transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
		transition: transform 0.18s ease;
	}

	&:focus-visible {
		outline: 2px solid ${PURPLE};
		outline-offset: -2px;
	}

	${pressedTint}
`;

const SectionText = styled.span`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-width: 0;

	strong {
		color: #303030;
		font-size: 14px;
		font-weight: 600;
	}
`;

const SectionSummary = styled.span<{ $active: boolean }>`
	overflow: hidden;
	color: ${({ $active }) => ($active ? PURPLE : "#9b9b9b")};
	font-size: 12px;
	font-weight: ${({ $active }) => ($active ? 600 : 400)};
	white-space: nowrap;
	text-overflow: ellipsis;
`;

const SectionBody = styled.div`
	padding: 0 16px 16px;
`;

const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;

const Chip = styled.button<{ $selected: boolean }>`
	min-height: 40px;
	padding: 0 16px;
	border: 1px solid ${({ $selected }) => ($selected ? PURPLE : "#cdd4f6")};
	border-radius: 999px;
	background: ${({ $selected }) => ($selected ? PURPLE : "#ffffff")};
	color: ${({ $selected }) => ($selected ? "#ffffff" : "#303030")};
	font: inherit;
	font-size: 14px;
	font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
	cursor: pointer;
	transition:
		background-color 0.15s ease,
		border-color 0.15s ease;

	&:focus-visible {
		outline: 2px solid ${PURPLE};
		outline-offset: 2px;
	}

	${pressedDarken}
`;

const OptionSearch = styled(Input)`
	&& {
		height: ${TAP_TARGET}px;
		margin-bottom: 4px;
		border-radius: 8px;
		font-size: 16px;
	}
`;

const OptionRow = styled(Checkbox)`
	display: flex;
	align-items: center;
	min-height: ${TAP_TARGET}px;
	margin: 0;
	font-size: 14px;

	&& + && {
		margin-inline-start: 0;
	}

	.ant-checkbox-checked .ant-checkbox-inner {
		border-color: ${PURPLE};
		background: ${PURPLE};
	}
`;

const OptionLabel = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
`;

const OptionFlag = styled.img`
	width: 18px;
	height: 18px;
	border-radius: 50%;
	object-fit: cover;
`;

const EmptyOptions = styled.p`
	margin: 8px 0 0;
	color: #9b9b9b;
	font-size: 13px;
`;

interface CampaignFilterSheetProps<T extends object> {
	columns: CustomTableColumn<T>[];
	dataSource?: readonly T[];
	filterState: TableFilterState;
	onFilterStateChange: (next: TableFilterState) => void;
}

type FilterGroup = { key: string; title: string; options: string[]; present: Set<string> };

const columnKeyOf = <T extends object>(column: CustomTableColumn<T>) =>
	String(getLooseColumnKey(column as Parameters<typeof getLooseColumnKey>[0]) ?? "");

const summarize = (selected: string[]) =>
	selected.length === 0 ? "Any" : selected.length <= 2 ? selected.join(", ") : `${selected.length} selected`;

/**
 * Phone access to the table's column filters. On phones the table shows only its primary
 * column, so the header filter dropdowns aren't reachable; this sheet edits the same
 * filter state, so header filters, pills and the URL stay in sync.
 */
export function CampaignFilterSheet<T extends object>({
	columns,
	dataSource,
	filterState,
	onFilterStateChange,
}: CampaignFilterSheetProps<T>) {
	const [open, setOpen] = useState(false);
	const [openGroup, setOpenGroup] = useState<string | null>(null);
	const [optionSearch, setOptionSearch] = useState<Record<string, string>>({});
	const rows = dataSource as T[] | undefined;
	const { filterOptionsByColumn, toggleFilterValue } = useTableFilters<T>({
		baseColumns: columns,
		dataSource: rows,
		externalFilterState: filterState,
		onFilterStateChange,
	});
	// Same filtering the table applies, so the button can say how many campaigns match.
	const matching = useProcessedData<T>({
		baseColumns: columns,
		dataSource: rows,
		filterState,
		sortState: null,
		searchTerm: "",
	});

	const groups = useMemo<FilterGroup[]>(
		() =>
			columns.flatMap((column) => {
				const key = columnKeyOf(column);
				const options = filterOptionsByColumn[key];
				if (!options || typeof column.title !== "string") return [];
				// Values that occur in this list; long lists show only these until the user searches.
				const present = new Set<string>();
				const dataIndex = getLooseDataIndex(column as Parameters<typeof getLooseDataIndex>[0]);
				(rows ?? []).forEach((row) =>
					toValueArray(getColumnValue(row, dataIndex)).forEach((value) => present.add(value)),
				);
				return [{ key, title: column.title, options, present }];
			}),
		[columns, filterOptionsByColumn, rows],
	);

	const activeCount = Object.values(filterState).reduce((sum, values) => sum + values.length, 0);

	if (groups.length === 0) return null;

	const openSheet = () => {
		// Open on the first filter in use, so the user sees what's applied.
		setOpenGroup(groups.find((group) => (filterState[group.key] ?? []).length > 0)?.key ?? null);
		setOpen(true);
	};

	const renderOptions = (group: FilterGroup) => {
		const selected = filterState[group.key] ?? [];

		if (group.options.length <= CHIP_OPTION_LIMIT) {
			return (
				<Chips role="group" aria-label={group.title}>
					{group.options.map((option) => (
						<Chip
							key={option}
							type="button"
							$selected={selected.includes(option)}
							aria-pressed={selected.includes(option)}
							onClick={() => toggleFilterValue(group.key, option)}
						>
							{option}
						</Chip>
					))}
				</Chips>
			);
		}

		const search = (optionSearch[group.key] ?? "").trim().toLowerCase();
		const visible = search
			? group.options.filter((option) => option.toLowerCase().includes(search))
			: group.options.filter((option) => group.present.has(option) || selected.includes(option));

		return (
			<>
				<OptionSearch
					placeholder={`Search ${group.title.toLowerCase()}`}
					aria-label={`Search ${group.title}`}
					value={optionSearch[group.key] ?? ""}
					onChange={(event) => setOptionSearch((prev) => ({ ...prev, [group.key]: event.target.value }))}
				/>
				{visible.map((option) => {
					const flag = group.key === "markets" ? getCountryFlagUrl(option) : "";
					return (
						<OptionRow
							key={option}
							checked={selected.includes(option)}
							onChange={() => toggleFilterValue(group.key, option)}
						>
							<OptionLabel>
								{flag && <OptionFlag src={flag} alt="" />}
								{option}
							</OptionLabel>
						</OptionRow>
					);
				})}
				{visible.length === 0 && <EmptyOptions>No matches.</EmptyOptions>}
			</>
		);
	};

	return (
		<>
			<FilterButton variant="outline" icon={<Icon name="tableFilter" size={18} />} onClick={openSheet}>
				Filter
				{activeCount > 0 && <ActiveCount aria-label={`${activeCount} active`}>{activeCount}</ActiveCount>}
			</FilterButton>
			<DynamicModal
				open={open}
				onCancel={() => setOpen(false)}
				onConfirm={() => setOpen(false)}
				title="Filter campaigns"
				confirmText={`Show ${matching.length} campaign${matching.length === 1 ? "" : "s"}`}
				singlePrimaryAction
				fullWidthActions
			>
				<SheetIntro>
					<span>
						{activeCount > 0
							? `${activeCount} filter${activeCount === 1 ? "" : "s"} applied`
							: "No filters applied"}
					</span>
					<ClearAll type="button" disabled={activeCount === 0} onClick={() => onFilterStateChange({})}>
						Clear all
					</ClearAll>
				</SheetIntro>
				<Sections>
					{groups.map((group) => {
						const isOpen = openGroup === group.key;
						const selected = filterState[group.key] ?? [];
						return (
							<Section key={group.key}>
								<SectionHeader
									type="button"
									$open={isOpen}
									aria-expanded={isOpen}
									onClick={() => setOpenGroup(isOpen ? null : group.key)}
								>
									<SectionText>
										<strong>{group.title}</strong>
										<SectionSummary $active={selected.length > 0}>
											{summarize(selected)}
										</SectionSummary>
									</SectionText>
									<ChevronDown size={18} aria-hidden />
								</SectionHeader>
								{isOpen && <SectionBody>{renderOptions(group)}</SectionBody>}
							</Section>
						);
					})}
				</Sections>
			</DynamicModal>
		</>
	);
}
